/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import InputField from "components/fields/InputField";
import { Select, Button } from "@chakra-ui/react";
import { Tooltip, IconButton } from "@mui/material";
import { MdOutlineContentCopy } from "react-icons/md";
import AutoComplete from "react-select";

const ReceiverDetailTab: React.FC<{
  detail: any;
  setSwalProps: any;
  countries: any;
  dialcodeList: any;
  dialcodeAutoCompleteOptions: any;
  countryAutoCompleteOptions: any;
  userPermissions: any;
}> = ({ detail, setSwalProps, countries, dialcodeList, dialcodeAutoCompleteOptions, countryAutoCompleteOptions, userPermissions }) => {
  const [receiverDetailForEdit, setReceiverDetailForEdit] = useState(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const dialcodeAutoCompleteRef = useRef(null);
  const countryAutoCompleteRef = useRef(null);
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const username = localStorage.getItem("username");

  useEffect(() => {
    setReceiverDetailForEdit(detail);
  }, [detail]);

  const handleClickCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const handleInputChange = async (event: any) => {
    const { id, value } = event.target;
    if (id === "phone") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setReceiverDetailForEdit({ ...receiverDetailForEdit, [id]: numericValue });
    } else {
      setReceiverDetailForEdit({ ...receiverDetailForEdit, [id]: value });
    }
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isFormValid =
    receiverDetailForEdit !== null
      ? receiverDetailForEdit.first_name &&
        receiverDetailForEdit.last_name &&
        receiverDetailForEdit.email &&
        isValidEmail(receiverDetailForEdit.email) &&
        receiverDetailForEdit.dob &&
        receiverDetailForEdit.dialcode &&
        receiverDetailForEdit.phone &&
        receiverDetailForEdit.address &&
        receiverDetailForEdit.city &&
        receiverDetailForEdit.state &&
        receiverDetailForEdit.postcode &&
        receiverDetailForEdit.country
      : false;
  const handleSubmit = async () => {
    if (isFormValid) {
      let receiverDetailData = JSON.parse(
        JSON.stringify(receiverDetailForEdit)
      );

      delete receiverDetailData["id"];
      delete receiverDetailData["account"];
      delete receiverDetailData["created_at"];
      delete receiverDetailData["kyc_status"];
      delete receiverDetailData["status"];
      delete receiverDetailData["applicant_id"];

      receiverDetailData.userRole = userRole;
      receiverDetailData.username = username;
      receiverDetailData.updateid = receiverDetailForEdit.id;

      try {
        setIsUpdating(true);
        await axios
          .post(
            process.env.REACT_APP_API_URL +
              `/api/${userRole}/receiver/action/update-receiver`,
            receiverDetailData
          )
          .then(function (response) {
            if (response && response.data?.status === "ok") {
              setSwalProps({
                show: true,
                icon: "success",
                title: "Success",
                html: "Receiver has been successfully updated",
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsUpdating(false);
                },
              });
            } else {
              setIsUpdating(false);
              setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: response.data.message,
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsUpdating(false);
                },
              });
            }
          })
          .catch(function (error) {
            setIsUpdating(false);
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: error.response.data.message,
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsUpdating(false);
              },
            });
          });
      } catch (error) {
        setIsUpdating(false);
        setSwalProps({
          show: true,
          icon: "error",
          title: "Oops!",
          html: "Something went wrong, please try again later.",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
            setIsUpdating(false);
          },
        });
      }
    }
  };

  const handleDialcodeAutoCompleteChange = (item: any) => {
    if (item !== null) {
      setReceiverDetailForEdit({ ...receiverDetailForEdit, dialcode: item.value });
    } else {
      setReceiverDetailForEdit({ ...receiverDetailForEdit, dialcode: "" });
    }
  };

  const handleCountryAutoCompleteChange = (item: any) => {
    if (item !== null) {
      setReceiverDetailForEdit({ ...receiverDetailForEdit, country: item.value });
    } else {
      setReceiverDetailForEdit({ ...receiverDetailForEdit, country: "" });
    }
  };

  const noDialcodeOptionsMessageRenderer = ({
    inputValue,
  }: {
    inputValue: string;
  }) => {
    return <div className="text-sm">Dialcode not found for "{inputValue}"</div>;
  };

  const noCountryOptionsMessageRenderer = ({
    inputValue,
  }: {
    inputValue: string;
  }) => {
    return <div className="text-sm">Country not found for "{inputValue}"</div>;
  };

  const getSelectedDialcodeOption = () => {
    if (receiverDetailForEdit?.dialcode && dialcodeAutoCompleteOptions) {
      return dialcodeAutoCompleteOptions.find(
        (option: any) => option.value === receiverDetailForEdit.dialcode
      );
    }
    return null;
  };

  const getSelectedCountryOption = () => {
    if (receiverDetailForEdit?.country && countryAutoCompleteOptions) {
      return countryAutoCompleteOptions.find(
        (option: any) => option.value === receiverDetailForEdit.country
      );
    }
    return null;
  };

  const preventTyping = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow Ctrl/Command + C (Copy)
    if ((e.ctrlKey || e.metaKey) && e.key === "c") {
      return; // Allow copy
    }

    // Prevent all other key presses
    e.preventDefault();
  };

  return (
    <div className="px-6 py-6">
      {receiverDetailForEdit !== null &&
      countries !== null &&
      dialcodeList !== null ? (
        <>
          {!userPermissions?.editReceiver && (
            <>
              {/* Desktop: 2-column grid */}
              <div className="hidden gap-3 px-1 text-sm md:grid md:grid-cols-2">
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">First Name</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.first_name}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Last Name</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.last_name}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Middle Name</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.middle_name !== ""
                      ? receiverDetailForEdit.middle_name
                      : "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Gender</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.gender !== "" &&
                    receiverDetailForEdit.gender
                      ? receiverDetailForEdit.gender === "M"
                        ? "Male"
                        : "Female"
                      : "-"}
                  </div>
                </div>

                {userPermissions?.editReceiverEmailOnly ? (
                  <div className="col-span-2">
                    <InputField
                      disabled={isUpdating}
                      variant=""
                      extra="shadow-sm w-[30%]"
                      label="Email"
                      required={true}
                      placeholder=""
                      id="email"
                      type="email"
                      autoComplete="off"
                      value={receiverDetailForEdit.email}
                      onChange={handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="flex">
                    <div className="w-32 shrink-0 text-navy-700 dark:text-white">Email</div>
                    <div className="flex items-center gap-1 font-semibold text-navy-700 dark:text-white">
                      {receiverDetailForEdit.email}
                      <Tooltip title="Copy Email" arrow placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleClickCopy(receiverDetailForEdit.email)}
                        >
                          <MdOutlineContentCopy className="h-3.5 w-3.5 text-navy-800" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                )}

                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Date of Birth</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.dob || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Dial Code</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.dialcode || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Phone</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.phone || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Address</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.address || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Address 2</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.address2 && receiverDetailForEdit.address2 !== ""
                      ? receiverDetailForEdit.address2
                      : "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">City</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.city || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">State</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.state && receiverDetailForEdit.state !== ""
                      ? receiverDetailForEdit.state
                      : "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Postcode</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.postcode || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Country</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.country || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Reference</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.reference && receiverDetailForEdit.reference !== ""
                      ? receiverDetailForEdit.reference
                      : "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">
                    <a
                      rel="noreferrer"
                      href={`https://cockpit.sumsub.com/checkus#/applicant/${receiverDetailForEdit.applicant_id}`}
                      target="_blank"
                      className="text-navy-600 underline hover:text-primary-700 dark:text-white"
                    >
                      Applicant ID
                    </a>
                  </div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.applicant_id || "-"}
                  </div>
                </div>
              </div>

              {/* Mobile: stacked layout */}
              <div className="flex flex-col gap-3 px-1 text-sm md:hidden">
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">First Name</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.first_name}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Last Name</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.last_name}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Middle Name</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.middle_name !== ""
                      ? receiverDetailForEdit.middle_name
                      : "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Gender</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.gender !== "" &&
                    receiverDetailForEdit.gender
                      ? receiverDetailForEdit.gender === "M"
                        ? "Male"
                        : "Female"
                      : "-"}
                  </div>
                </div>

                {userPermissions?.editReceiverEmailOnly ? (
                  <div>
                    <InputField
                      disabled={isUpdating}
                      variant=""
                      extra="shadow-sm"
                      label="Email"
                      required={true}
                      placeholder=""
                      id="email"
                      type="email"
                      autoComplete="off"
                      value={receiverDetailForEdit.email}
                      onChange={handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="flex">
                    <div className="w-28 shrink-0 text-navy-700 dark:text-white">Email</div>
                    <div className="flex items-center gap-1 font-semibold text-navy-700 dark:text-white">
                      {receiverDetailForEdit.email}
                      <Tooltip title="Copy Email" arrow placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleClickCopy(receiverDetailForEdit.email)}
                        >
                          <MdOutlineContentCopy className="h-3.5 w-3.5 text-navy-800" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                )}

                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Date of Birth</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.dob || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Dial Code</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.dialcode || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Phone</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.phone || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Address</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.address || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Address 2</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.address2 && receiverDetailForEdit.address2 !== ""
                      ? receiverDetailForEdit.address2
                      : "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">City</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.city || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">State</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.state && receiverDetailForEdit.state !== ""
                      ? receiverDetailForEdit.state
                      : "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Postcode</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.postcode || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Country</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.country || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Reference</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.reference && receiverDetailForEdit.reference !== ""
                      ? receiverDetailForEdit.reference
                      : "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">
                    <a
                      rel="noreferrer"
                      href={`https://cockpit.sumsub.com/checkus#/applicant/${receiverDetailForEdit.applicant_id}`}
                      target="_blank"
                      className="text-navy-600 underline hover:text-primary-700 dark:text-white"
                    >
                      Applicant ID
                    </a>
                  </div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {receiverDetailForEdit.applicant_id || "-"}
                  </div>
                </div>
              </div>

              {userPermissions?.editReceiverEmailOnly && (
                <div className="flex flex-wrap justify-end">
                  <Button
                    onClick={() => handleSubmit()}
                    isDisabled={!isFormValid || isUpdating}
                    isLoading={isUpdating}
                    loadingText="Updating Receiver..."
                    className="btn-primary relative overflow-hidden rounded-md px-8 py-2.5 font-semibold text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span className="relative z-10 tracking-tight">
                      Update Receiver
                    </span>
                    <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
                  </Button>
                </div>
              )}
            </>
          )}
          {userPermissions?.editReceiver && (
            <>
              <div className="flex flex-wrap justify-between">
                <div className="w-full md:w-1/2 md:pr-4">
                  <InputField
                    disabled={isUpdating}
                    variant="auth"
                    extra="mb-4 shadow-sm"
                    label="First Name"
                    required={true}
                    placeholder=""
                    id="first_name"
                    type="text"
                    autoComplete="off"
                    value={receiverDetailForEdit.first_name}
                    onChange={handleInputChange}
                  />

                  <InputField
                    disabled={isUpdating}
                    variant="auth"
                    extra="mb-4 shadow-sm"
                    label="Middle Name"
                    placeholder=""
                    id="middle_name"
                    type="text"
                    autoComplete="off"
                    value={receiverDetailForEdit.middle_name}
                    onChange={handleInputChange}
                  />

                  <InputField
                    disabled={isUpdating}
                    variant="auth"
                    extra="mb-4 shadow-sm"
                    label="Last Name"
                    required={true}
                    placeholder=""
                    id="last_name"
                    type="text"
                    autoComplete="off"
                    value={receiverDetailForEdit.last_name}
                    onChange={handleInputChange}
                  />

                  <label className="mb-1 text-sm font-medium text-navy-700 dark:text-white">
                    Gender
                  </label>

                  <Select
                    disabled={isUpdating}
                    id="gender"
                    value={receiverDetailForEdit.gender}
                    onChange={handleInputChange}
                    className="custom-select relative mb-4 h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:outline-none sm:text-sm"
                  >
                    <option value="">-- Please Select --</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                  </Select>

                  <InputField
                    disabled={isUpdating}
                    variant="auth"
                    extra="mb-4 shadow-sm"
                    label="Email"
                    required={true}
                    placeholder=""
                    id="email"
                    type="email"
                    autoComplete="off"
                    value={receiverDetailForEdit.email}
                    onChange={handleInputChange}
                  />
                  <InputField
                    disabled={isUpdating}
                    variant="auth"
                    extra="mb-4 shadow-sm h-[42px]"
                    label="Date of Birth"
                    required={true}
                    placeholder=""
                    id="dob"
                    type="date"
                    autoComplete="off"
                    value={receiverDetailForEdit.dob}
                    onChange={handleInputChange}
                  />

                  <label className="mb-1 text-sm font-medium text-navy-700 dark:text-white">
                    Dialcode <span className="text-red-500">*</span>
                  </label>

                  <AutoComplete
                    ref={dialcodeAutoCompleteRef}
                    isDisabled={isUpdating}
                    placeholder="Please Select"
                    options={dialcodeAutoCompleteOptions}
                    value={getSelectedDialcodeOption()}
                    classNamePrefix="custom-autocomplete"
                    className="autocomplete-override mb-4"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                    onChange={(item: any) => handleDialcodeAutoCompleteChange(item)}
                    noOptionsMessage={noDialcodeOptionsMessageRenderer}
                    isClearable
                  />

                  <InputField
                    disabled={isUpdating}
                    variant="auth"
                    extra="mb-4 shadow-sm"
                    label="Phone"
                    required={true}
                    placeholder=""
                    id="phone"
                    type="text"
                    autoComplete="off"
                    value={receiverDetailForEdit.phone}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="w-full md:w-1/2 md:pl-4">
                  <InputField
                    disabled={isUpdating}
                    variant="auth"
                    extra="mb-4 shadow-sm"
                    label="Address"
                    required={true}
                    placeholder=""
                    id="address"
                    type="text"
                    autoComplete="off"
                    value={receiverDetailForEdit.address}
                    onChange={handleInputChange}
                  />

                  <InputField
                    disabled={isUpdating}
                    variant="auth"
                    extra="mb-4 shadow-sm"
                    label="Address 2"
                    placeholder=""
                    id="address2"
                    type="text"
                    autoComplete="off"
                    value={receiverDetailForEdit.address2}
                    onChange={handleInputChange}
                  />

                  <InputField
                    disabled={isUpdating}
                    variant="auth"
                    extra="mb-4 shadow-sm"
                    label="City"
                    required={true}
                    placeholder=""
                    id="city"
                    type="text"
                    autoComplete="off"
                    value={receiverDetailForEdit.city}
                    onChange={handleInputChange}
                  />

                  <InputField
                    disabled={isUpdating}
                    variant="auth"
                    extra="mb-4 shadow-sm"
                    label="State"
                    placeholder=""
                    id="state"
                    type="text"
                    autoComplete="off"
                    value={receiverDetailForEdit.state}
                    onChange={handleInputChange}
                  />

                  <InputField
                    disabled={isUpdating}
                    variant="auth"
                    extra="mb-4 shadow-sm"
                    label="Postcode"
                    required={true}
                    placeholder=""
                    id="postcode"
                    type="text"
                    autoComplete="off"
                    value={receiverDetailForEdit.postcode}
                    onChange={handleInputChange}
                  />

                  <label className="text-sm font-medium text-navy-700 dark:text-white">
                    Country <span className="text-red-500">*</span>
                  </label>

                  <AutoComplete
                    ref={countryAutoCompleteRef}
                    isDisabled={isUpdating}
                    placeholder="Please Select"
                    options={countryAutoCompleteOptions}
                    value={getSelectedCountryOption()}
                    classNamePrefix="custom-autocomplete"
                    className="autocomplete-override mb-4"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                    onChange={(item: any) => handleCountryAutoCompleteChange(item)}
                    noOptionsMessage={noCountryOptionsMessageRenderer}
                    isClearable
                  />

                  <InputField
                    disabled={isUpdating}
                    variant="auth"
                    extra="mb-4 shadow-sm"
                    label="Reference"
                    placeholder=""
                    id="reference"
                    type="text"
                    autoComplete="off"
                    value={receiverDetailForEdit.reference}
                    onChange={handleInputChange}
                  />

                  {receiverDetailForEdit.applicant_id && (
                    <InputField
                      variant="auth"
                      extra="mb-4 shadow-sm"
                      label="Sumsub Applicant ID"
                      labelAsLink={`https://cockpit.sumsub.com/checkus#/applicant/${receiverDetailForEdit.applicant_id}`}
                      placeholder=""
                      id="sumsub_link"
                      type="text"
                      autoComplete="off"
                      value={receiverDetailForEdit.applicant_id}
                      onChange={handleInputChange}
                      onKeyDown={preventTyping}
                    />
                  )}

                </div>
              </div>
              <div className="flex flex-wrap justify-end">
                <Button
                  onClick={() => handleSubmit()}
                  isDisabled={!isFormValid || isUpdating}
                  isLoading={isUpdating}
                  loadingText="Updating Receiver..."
                  className="btn-primary relative overflow-hidden rounded-md px-8 py-2.5 font-semibold text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="relative z-10 tracking-tight">
                    Update Receiver
                  </span>
                  <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
                </Button>
              </div>
            </>
          )}
        </>
      ) : (
        <div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
        </div>
      )}
    </div>
  );
};

export default ReceiverDetailTab;
